import type { AnyExtension, Editor } from "@tiptap/core";
import { useEditor } from "@tiptap/react";

import { Heading, ImageBlock, ImageUpload, Paragraph } from "@/extensions";
import { ExtensionKit } from "@/extensions/extension-kit";

import { DOMParser as ProseMirrorDOMParser } from "prosemirror-model";
import sanitizeHtml from "sanitize-html";

declare global {
  interface Window {
    editor: Editor | null;
  }
}

export const extensions = [
  ...ExtensionKit(),
  Heading.configure({
    levels: [1],
    HTMLAttributes: {
      style:
        "color: #525753; font-size: 18px; font-style: normal; font-weight: 700; line-height: 30px; margin-bottom: 16px;",
    },
  }),
  Paragraph.configure({
    HTMLAttributes: {
      style:
        "color: #525753; font-size: 16px; font-style: normal; font-weight: 400; line-height: 20px;",
    },
  }),
  ImageUpload,
  ImageBlock.configure({
    HTMLAttributes: {
      style: "border-radius: 10px;",
    },
  }),
].filter((e): e is AnyExtension => e !== undefined);

export const useBlockEditor = (initialContent?: any, customExtensions = []) => {
  const editor = useEditor(
    {
      immediatelyRender: true,
      shouldRerenderOnTransaction: false,
      autofocus: false,
      onCreate: (ctx) => {
        if (initialContent) {
          ctx.editor.commands.setContent(initialContent, true);
        }
        //ctx.editor.commands.focus('start', { scrollIntoView: true })
      },
      extensions: [...extensions, ...customExtensions],
      editorProps: {
        handlePaste(view, event, slice) {
          const html = event.clipboardData?.getData("text/html");
          if (!html) return false; // deixa o comportamento padrão

          const cleanedHTML = sanitizeHtml(html, {
            allowedTags: false,
            allowedAttributes: false,
            transformTags: {
              "*": (tagName, attribs) => {
                const unwanted = ["style", "class", "color", "face", "size"];
                unwanted.forEach((attr) => delete attribs[attr]);
                return { tagName, attribs };
              },
            },
          });

          // 2. Converte HTML para fragmento DOM
          const parser = new DOMParser();
          const doc = parser.parseFromString(cleanedHTML, "text/html");
          const fragment = ProseMirrorDOMParser.fromSchema(
            view.state.schema
          ).parseSlice(doc.body);

          // 3. Insere o conteúdo limpo
          const transaction = view.state.tr.replaceSelection(fragment);
          view.dispatch(transaction);

          event.preventDefault();
          return true;
        },
      },
    },
    []
  );

  window.editor = editor;

  return { editor };
};
